import { useState } from 'react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  parseISO,
} from 'date-fns';
import type { Appointment } from '../../types';

interface CalendarProps {
  appointments: Appointment[];
  physios: Array<{ id: number; name: string }>;
  patients: Array<{ id: number; name: string }>;
  onDateSelect?: (date: Date) => void;
  onAppointmentClick?: (appointment: Appointment) => void;
}

type ViewMode = 'month' | 'week';

export function Calendar({
  appointments,
  physios,
  patients: _patients,
  onDateSelect,
  onAppointmentClick,
}: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [selectedPhysio, setSelectedPhysio] = useState<number | 'all'>('all');

  const goToPrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get days to display based on view mode
  const getDays = () => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    } else {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }
  };

  const days = getDays();

  // Filter appointments by selected physio
  const filteredAppointments = appointments.filter((apt) => {
    if (selectedPhysio === 'all') return true;
    return apt.physio_id === selectedPhysio;
  });

  // Get appointments for a specific day
  const getAppointmentsForDay = (day: Date) => {
    return filteredAppointments.filter((apt) => {
      const aptDate = parseISO(apt.start_time);
      return isSameDay(aptDate, day);
    });
  };

  // Get color for physio (consistent colors)
  const getPhysioColor = (physioId: number | null) => {
    const colors = [
      'bg-blue-100 border-blue-300 text-blue-800',
      'bg-green-100 border-green-300 text-green-800',
      'bg-purple-100 border-purple-300 text-purple-800',
      'bg-orange-100 border-orange-300 text-orange-800',
      'bg-pink-100 border-pink-300 text-pink-800',
      'bg-teal-100 border-teal-300 text-teal-800',
    ];
    if (!physioId) return 'bg-gray-100 border-gray-300 text-gray-800';
    return colors[(physioId - 1) % colors.length];
  };

  // Time slots for week view
  const timeSlots = [];
  for (let h = 8; h <= 18; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Calendar Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {viewMode === 'month'
                ? format(currentDate, 'MMMM yyyy')
                : `Week of ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}`}
            </h2>
            <div className="flex items-center space-x-1">
              <button
                onClick={goToPrevious}
                className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Today
              </button>
              <button
                onClick={goToNext}
                className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Physio Filter */}
            <select
              value={selectedPhysio}
              onChange={(e) => setSelectedPhysio(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Physios</option>
              {physios.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'week'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'month'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Month
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Physio Legend */}
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-2">
        {physios.map((physio) => (
          <span
            key={physio.id}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPhysioColor(physio.id)}`}
          >
            {physio.name}
          </span>
        ))}
      </div>

      {viewMode === 'month' ? (
        /* Month View */
        <div className="p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const dayAppointments = getAppointmentsForDay(day);
              const isToday = isSameDay(day, new Date());
              const isCurrentMonth = isSameMonth(day, currentDate);

              return (
                <div
                  key={day.toISOString()}
                  onClick={() => onDateSelect?.(day)}
                  className={`min-h-[100px] p-1 border rounded-lg cursor-pointer transition-colors ${
                    isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                  } ${isToday ? 'border-primary-500 border-2' : 'border-gray-200'} hover:bg-gray-50`}
                >
                  <div
                    className={`text-sm font-medium mb-1 ${
                      isToday
                        ? 'text-primary-600'
                        : isCurrentMonth
                          ? 'text-gray-900'
                          : 'text-gray-400'
                    }`}
                  >
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5 overflow-y-auto max-h-[70px]">
                    {dayAppointments.slice(0, 3).map((apt) => (
                      <div
                        key={apt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick?.(apt);
                        }}
                        className={`text-xs px-1 py-0.5 rounded truncate border ${getPhysioColor(apt.physio_id)}`}
                        title={`${apt.patient_name} - ${apt.physio_name || 'Unassigned'}`}
                      >
                        {format(parseISO(apt.start_time), 'HH:mm')} {apt.patient_name?.split(' ')[0]}
                      </div>
                    ))}
                    {dayAppointments.length > 3 && (
                      <div className="text-xs text-gray-500 pl-1">
                        +{dayAppointments.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Week View */
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Day Headers */}
            <div className="grid grid-cols-8 border-b border-gray-200">
              <div className="p-2 text-sm font-medium text-gray-500 border-r border-gray-200">
                Time
              </div>
              {days.map((day) => {
                const isToday = isSameDay(day, new Date());
                return (
                  <div
                    key={day.toISOString()}
                    className={`p-2 text-center border-r border-gray-200 last:border-r-0 ${
                      isToday ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-500">
                      {format(day, 'EEE')}
                    </div>
                    <div
                      className={`text-lg font-semibold ${
                        isToday ? 'text-primary-600' : 'text-gray-900'
                      }`}
                    >
                      {format(day, 'd')}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Time Grid */}
            <div className="relative">
              {timeSlots.map((time) => (
                <div key={time} className="grid grid-cols-8 border-b border-gray-100">
                  <div className="p-2 text-xs text-gray-500 border-r border-gray-200 bg-gray-50">
                    {time}
                  </div>
                  {days.map((day) => {
                    const dayAppointments = filteredAppointments.filter((apt) => {
                      const aptStart = parseISO(apt.start_time);
                      const slotStart = new Date(day);
                      const [hours] = time.split(':').map(Number);
                      slotStart.setHours(hours, 0, 0, 0);
                      const slotEnd = new Date(slotStart);
                      slotEnd.setHours(hours + 1, 0, 0, 0);

                      return (
                        isSameDay(aptStart, day) &&
                        aptStart >= slotStart &&
                        aptStart < slotEnd
                      );
                    });

                    return (
                      <div
                        key={`${day.toISOString()}-${time}`}
                        className="p-1 min-h-[60px] border-r border-gray-100 last:border-r-0 relative"
                      >
                        {dayAppointments.map((apt) => (
                          <div
                            key={apt.id}
                            onClick={() => onAppointmentClick?.(apt)}
                            className={`text-xs p-1.5 rounded border mb-1 cursor-pointer hover:opacity-80 transition-opacity ${getPhysioColor(apt.physio_id)}`}
                          >
                            <div className="font-medium truncate">{apt.patient_name}</div>
                            <div className="text-[10px] opacity-75 truncate">
                              {apt.physio_name || 'Unassigned'}
                            </div>
                            <div className="text-[10px] opacity-75">
                              {format(parseISO(apt.start_time), 'HH:mm')} -{' '}
                              {format(parseISO(apt.end_time), 'HH:mm')}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredAppointments.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          <svg
            className="w-12 h-12 mx-auto mb-3 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p>No appointments scheduled</p>
          <p className="text-sm mt-1">Appointments will appear here once created</p>
        </div>
      )}
    </div>
  );
}
